import React, { useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import LineIcon from '../components/LineIcon';
import { useGarage } from './GarageContext';
import { firstName, formatDayMonthYear, formatKm, formatMoney, formatPlate, plural } from './format';

const SELECTED_KEY = 'SR_GARAGE_SELECTED';

const readSelected = () => {
  try {
    return localStorage.getItem(SELECTED_KEY) || '';
  } catch (err) {
    return '';
  }
};

const saveSelected = (plate) => {
  try {
    localStorage.setItem(SELECTED_KEY, plate);
  } catch (err) {
    // sem armazenamento a escolha vale so ate recarregar a pagina
  }
};

const vehicleName = (vehicle) => [vehicle.brand, vehicle.model].filter(Boolean).join(' ') || 'Veículo';

const historyPath = (vehicle) => `/client/maintances?placa=${encodeURIComponent(vehicle.licensePlate)}`;

// Veiculo mostrado: o escolhido em "Trocar" ou o de atendimento mais recente.
const pickCurrent = (vehicles, plate) => {
  const chosen = vehicles.find((v) => v.licensePlate === plate);
  if (chosen) return chosen;
  return [...vehicles].sort((a, b) => {
    const da = a.lastRecord && a.lastRecord.date ? a.lastRecord.date.getTime() : 0;
    const db = b.lastRecord && b.lastRecord.date ? b.lastRecord.date.getTime() : 0;
    return db - da;
  })[0];
};

// Checklist do veiculo (estilo "Licenciamento em dia"), so com dados que a API entrega.
function StatusList({ vehicle }) {
  const { records, lastRecord, pendingEvaluations, failed } = vehicle;
  const lastKm = records.find((r) => r.km);
  const items = [];

  if (failed) {
    items.push({ tone: 'warn', text: 'Histórico indisponível agora' });
  } else if (!records.length) {
    items.push({ tone: 'idle', text: 'Nenhum atendimento registrado ainda' });
  } else {
    items.push({ tone: 'ok', text: `${records.length} ${plural(records.length, 'atendimento registrado', 'atendimentos registrados')}` });
    items.push({ tone: 'ok', text: `Último em ${formatDayMonthYear(lastRecord.date)}, ${lastRecord.company.name}` });
    if (lastKm) items.push({ tone: 'ok', text: `Última quilometragem: ${formatKm(lastKm.km)}` });
  }
  if (pendingEvaluations > 0) {
    items.push({ tone: 'attention', text: `${pendingEvaluations} ${plural(pendingEvaluations, 'atendimento para avaliar', 'atendimentos para avaliar')}` });
  }

  return (
    <ul className='gs-mv-status'>
      {items.map((item) => (
        <li key={item.text} className={`is-${item.tone}`}>
          <span className='gs-mv-status-icon' aria-hidden='true' />
          {item.text}
        </li>
      ))}
    </ul>
  );
}

// Card pequeno de outro veiculo, 2 por linha: placa, modelo e "Selecionar".
function OtherVehicle({ vehicle, onSelect }) {
  return (
    <li className='gs-ov'>
      <span className='gs-ov-plate'>{formatPlate(vehicle.licensePlate)}</span>
      <span className='gs-ov-model'>{vehicleName(vehicle)}</span>
      <button
        type='button'
        className='gs-ov-select'
        onClick={() => onSelect(vehicle)}
        aria-label={`Selecionar ${vehicleName(vehicle)}, placa ${formatPlate(vehicle.licensePlate)}`}
      >
        Selecionar
      </button>
    </li>
  );
}

const SHORTCUTS = [
  { to: '/client/maintances', icon: 'history', label: 'Histórico' },
  { to: '/client/documents', icon: 'file', label: 'Documentos' },
  { to: '/client/profile', icon: 'user', label: 'Perfil' },
];

// Minha garagem: veiculo selecionado em destaque, outros em cards (2 por linha) e atalhos.
export default function GaragePage() {
  const history = useHistory();
  const { loading, vehicles, error, partial, reload } = useGarage();
  const [plate, setPlate] = useState(readSelected);
  const name = firstName(localStorage.getItem('NOME_USUARIO'));

  const current = vehicles.length ? pickCurrent(vehicles, plate) : null;
  const total = current ? current.records.reduce((sum, r) => sum + (r.total || 0), 0) : 0;

  const others = current ? vehicles.filter((v) => v !== current) : [];

  const select = (vehicle) => {
    setPlate(vehicle.licensePlate);
    saveSelected(vehicle.licensePlate);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='gs-stack gs-garage'>
      <header className='gs-page-header'>
        <span className='gs-eyebrow'>{name ? `Olá, ${name}!` : 'Olá!'}</span>
        <h1>Minha garagem</h1>
      </header>

      {loading && <div className='gs-skeleton gs-garage-skeleton' aria-busy='true' aria-label='Carregando sua garagem' />}

      {!loading && error && (
        <section className='gs-empty' role='alert'>
          <h2>Não foi possível carregar sua garagem</h2>
          <p>Tente novamente em alguns instantes.</p>
          <button type='button' className='gs-btn gs-btn-primary' onClick={reload}>Tentar novamente</button>
        </section>
      )}

      {!loading && !error && !current && (
        <section className='gs-empty'>
          <h2>Sua garagem está pronta para começar</h2>
          <p>Adicione um veículo para acompanhar a história de manutenções e atendimentos.</p>
          <button type='button' className='gs-btn gs-btn-primary' onClick={() => history.push('/client/newvehicle')}>
            Adicionar meu primeiro veículo
          </button>
        </section>
      )}

      {!loading && !error && current && (
        <>
          {partial && <p className='gs-notice'>Não conseguimos consultar o histórico de todos os veículos agora.</p>}

          <section className='gs-mv' aria-labelledby='gs-mv-title'>
            <div className='gs-mv-head'>
              <h2 id='gs-mv-title'>
                <LineIcon name='car' size={18} />
                Veículo selecionado
              </h2>
            </div>

            <div className='gs-mv-card'>
              <Link to={historyPath(current)} className='gs-mv-id'>
                <span className='gs-mv-plate'>{formatPlate(current.licensePlate)}</span>
                <LineIcon name='chevronRight' size={18} />
              </Link>
              <p className='gs-mv-model'>{vehicleName(current)}</p>

              <StatusList vehicle={current} />

              {current.records.length > 0 && (
                <>
                  <hr className='gs-mv-divider' />
                  <div className='gs-mv-total'>
                    <span>Total registrado</span>
                    <span>{formatMoney(total)}</span>
                  </div>
                </>
              )}

              <Link to={historyPath(current)} className='gs-btn gs-btn-primary gs-mv-cta'>
                Ver histórico
                <LineIcon name='chevronRight' size={18} />
              </Link>
            </div>
          </section>

          <section aria-labelledby='gs-others-title'>
            <h2 id='gs-others-title' className='gs-section-title'>
              {others.length ? 'Outros veículos' : 'Adicionar outro veículo'}
            </h2>
            <ul className='gs-ov-grid'>
              {others.map((vehicle, index) => (
                <OtherVehicle key={vehicle.id || `${vehicle.licensePlate}-${index}`} vehicle={vehicle} onSelect={select} />
              ))}
              <li>
                <Link to='/client/newvehicle' className='gs-ov-add'>
                  <LineIcon name='plus' size={20} />
                  Adicionar veículo
                </Link>
              </li>
            </ul>
          </section>

          <section aria-labelledby='gs-shortcuts-title'>
            <h2 id='gs-shortcuts-title' className='gs-section-title'>Atalhos</h2>
            <ul className='gs-shortcuts'>
              {SHORTCUTS.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className='gs-shortcut'>
                    <span className='gs-shortcut-icon'><LineIcon name={item.icon} size={22} /></span>
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

    </div>
  );
}
